import React, { useState, useEffect } from 'react';
import { getAllFormFeedback, getFeedbackStats } from '../utils/formFeedback';
import AdminLayout from '../layouts/AdminLayout';
import { FiMessageSquare, FiStar, FiMail, FiAlertCircle, FiX, FiPieChart } from 'react-icons/fi';

const Feedback = () => {
  const [feedbackList, setFeedbackList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');

    const [feedbackResult, statsResult] = await Promise.all([
      getAllFormFeedback(),
      getFeedbackStats()
    ]);

    if (feedbackResult.success) {
      setFeedbackList(feedbackResult.data);
    } else {
      setError('فشل تحميل الملاحظات');
    }

    if (statsResult.success) {
      setStats(statsResult.stats);
    }

    setLoading(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ar-EG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <FiStar
            key={star}
            size={16}
            className={star <= rating ? 'fill-yellow text-yellow' : 'text-white/30'}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow mx-auto mb-4"></div>
            <p className="text-yellow">جاري التحميل...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-center bg-dark-blue border-2 border-yellow p-8 rounded-2xl max-w-md">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiAlertCircle className="text-yellow" size={32} />
            </div>
            <h2 className="text-xl font-semibold mb-2 text-yellow">خطأ</h2>
            <p className="text-white/80 mb-4">{error}</p>
            <button
              onClick={fetchData}
              className="px-6 py-2 bg-yellow text-[#05339c] rounded-lg hover:bg-yellow/80 transition-all duration-300 font-semibold"
            >
              إعادة المحاولة
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-yellow mb-2">
          ملاحظات <span className="text-white">المستخدمين</span>
        </h1>
        <p className="text-white/70">آراء المستخدمين حول النموذج والموقع</p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-yellow p-3 rounded-lg">
                <FiMessageSquare size={28} className="text-[#05339c]" />
              </div>
            </div>
            <p className="text-4xl font-bold mb-2 text-yellow">{stats.total}</p>
            <p className="text-white/70">إجمالي الملاحظات</p>
          </div>

          <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-yellow p-3 rounded-lg">
                <FiStar size={28} className="text-[#05339c]" />
              </div>
            </div>
            <p className="text-4xl font-bold mb-2 text-yellow">{stats.averageRating}</p>
            <p className="text-white/70">متوسط التقييم</p>
          </div>

          <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-yellow p-3 rounded-lg">
                <FiPieChart size={28} className="text-[#05339c]" />
              </div>
            </div>
            <p className="text-4xl font-bold mb-2 text-yellow">{stats.ratingCount}</p>
            <p className="text-white/70">عدد التقييمات</p>
          </div>

          <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-yellow p-3 rounded-lg">
                <FiMail size={28} className="text-[#05339c]" />
              </div>
            </div>
            <p className="text-4xl font-bold mb-2 text-yellow">
              {feedbackList.filter(f => f.email).length}
            </p>
            <p className="text-white/70">مع بريد إلكتروني</p>
          </div>
        </div>
      )}

      {/* Most Common Reasons */}
      {stats && stats.reasonCount && Object.keys(stats.reasonCount).length > 0 && (
        <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6 mb-8">
          <h2 className="text-xl font-bold text-yellow mb-6">الأسباب الأكثر شيوعاً</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(stats.reasonCount)
              .sort((a, b) => b[1] - a[1])
              .map(([reason, count]) => (
                <div key={reason} className="bg-blue/20 rounded-xl p-4 border-2 border-white/10">
                  <div className="text-sm font-medium text-white mb-1">{reason}</div>
                  <div className="text-3xl font-bold text-yellow">{count}</div>
                  <div className="text-xs text-white/60 mt-1">
                    {stats.total > 0 ? Math.round((count / stats.total) * 100) : 0}% من المجموع
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Feedback List */}
      <div className="bg-dark-blue border-2 border-yellow rounded-2xl overflow-hidden">
        <div className="px-6 py-4 bg-yellow">
          <h2 className="text-xl font-bold text-[#05339c]">جميع الملاحظات ({feedbackList.length})</h2>
        </div>

        {feedbackList.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiMessageSquare className="text-white/50" size={32} />
            </div>
            <p className="text-white mb-2">لا توجد ملاحظات</p>
            <p className="text-white/60 text-sm">لم يترك المستخدمون أي ملاحظات بعد</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10">
              <thead className="bg-blue">
                <tr>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-yellow uppercase tracking-wider">
                    السبب
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-yellow uppercase tracking-wider">
                    التقييم
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-yellow uppercase tracking-wider">
                    البريد الإلكتروني
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-yellow uppercase tracking-wider">
                    التاريخ
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-yellow uppercase tracking-wider">
                    تفاصيل
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {feedbackList.map((feedback) => (
                  <tr key={feedback.id} className="hover:bg-blue/20 transition-colors">
                    <td className="px-6 py-4">
                      <span className="text-white font-medium">{feedback.reason || '-'}</span>
                    </td>
                    <td className="px-6 py-4">
                      {feedback.rating ? renderStars(feedback.rating) : <span className="text-white/40">-</span>}
                    </td>
                    <td className="px-6 py-4">
                      {feedback.email ? (
                        <div className="flex items-center gap-2">
                          <FiMail className="text-yellow" size={16} />
                          <span className="text-white/80 text-sm">{feedback.email}</span>
                        </div>
                      ) : (
                        <span className="text-white/40">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-white/60">
                      {formatDate(feedback.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => setSelectedFeedback(feedback)}
                        className="px-3 py-2 bg-blue text-yellow border-2 border-yellow rounded-lg hover:bg-blue/80 transition-colors font-medium text-sm"
                      >
                        عرض
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Feedback Details Modal */}
      {selectedFeedback && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-yellow px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-[#05339c]">تفاصيل الملاحظة</h3>
              <button
                onClick={() => setSelectedFeedback(null)}
                className="text-[#05339c] hover:bg-[#05339c] hover:text-yellow p-2 rounded-lg transition-all"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="p-6">
              <div className="bg-blue rounded-xl p-4 border-2 border-white/10 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-white/50 mb-1">السبب</label>
                    <div className="text-base font-medium text-white">{selectedFeedback.reason || '-'}</div>
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1">التقييم</label>
                    <div className="text-base font-medium text-white">
                      {selectedFeedback.rating ? (
                        <div className="flex items-center gap-2">
                          {renderStars(selectedFeedback.rating)}
                          <span className="text-yellow">({selectedFeedback.rating}/5)</span>
                        </div>
                      ) : '-'}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-white/50 mb-1">البريد الإلكتروني</label>
                    <div className="text-base font-medium text-white">{selectedFeedback.email || '-'}</div>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-white/50 mb-1">الملاحظات الإضافية</label>
                    <div className="text-base font-medium text-white whitespace-pre-wrap">
                      {selectedFeedback.feedback_text || 'لا توجد ملاحظات إضافية'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue rounded-xl p-4 border-2 border-white/10">
                <h4 className="text-sm font-medium text-yellow mb-3">معلومات إضافية</h4>
                <div className="text-xs text-white/60 space-y-2">
                  <div>Session ID: {selectedFeedback.session_id}</div>
                  <div>تم الإنشاء: {formatDate(selectedFeedback.created_at)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Feedback;

